/**
 * AbandonCartSequenceConfig — secuencia de envíos de recuperación de carrito abandonado.
 *
 * Cada paso sale a su tiempo desde el abandono y tiene sus propias plantillas, que se
 * reconocen por el nombre: el paso `default` usa las de siempre (`ac`) y el resto
 * `ac_step_<key>`. El backend busca la plantilla de cada paso con esta MISMA regla
 * (`AbandonedCheckoutStep.templateFilter`); si cambia aquí, hay que cambiarla allí.
 *
 * La secuencia se guarda en el `checkout-scheduler` de la skill y el backend calcula
 * cada cuánto busca carritos a partir del primer paso activo.
 *
 * @module AbandonCartSequenceConfig
 */

export const DEFAULT_STEP_KEY = 'default';
const DEFAULT_TEMPLATE_NAME = 'ac';
const STEP_TEMPLATE_PREFIX = 'ac_step_';

export const MIN_STEP_DELAY_MINUTES = 10;
/** Una semana: el backend no acepta más. */
export const MAX_STEP_DELAY_MINUTES = 7 * 24 * 60;
export const MAX_STEPS = 10;

/**
 * Pasos base: un único envío a los 10 minutos y los de 12 h y 23 h ofrecidos pero apagados.
 * @returns {Array<{key: string, delay_minutes: number, enabled: boolean}>}
 */
export function defaultSteps() {
    return [
        { key: DEFAULT_STEP_KEY, delay_minutes: 10, enabled: true },
        { key: 'h12', delay_minutes: 12 * 60, enabled: false },
        { key: 'h23', delay_minutes: 23 * 60, enabled: false },
    ];
}

/**
 * @param {string} key
 * @returns {string} Nombre con el que se crean las plantillas del paso.
 */
export function templateNameForStep(key) {
    return key === DEFAULT_STEP_KEY ? DEFAULT_TEMPLATE_NAME : `${STEP_TEMPLATE_PREFIX}${key}`;
}

/**
 * El paso por defecto se queda con cualquier plantilla que no sea de otro paso: las antiguas
 * no siempre se llamaban `ac`.
 *
 * @param {{templateName?: string}} template
 * @param {string} key
 * @returns {boolean}
 */
export function templateBelongsToStep(template, key) {
    const name = template?.templateName;
    if (key === DEFAULT_STEP_KEY) {
        return !name || !name.startsWith(STEP_TEMPLATE_PREFIX);
    }
    return name === templateNameForStep(key);
}

/** @returns {string} Clave nueva para un paso añadido por el cliente (`[a-z0-9]`). */
export function newStepKey() {
    return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * @param {Array<{key: string, delay_minutes: number, enabled: boolean}>} steps
 * @returns {string|null} Clave de i18n del error, o null si la secuencia es válida.
 */
export function validateSteps(steps) {
    if (!steps.some((step) => step.enabled)) return 'skills.abandonCart.sequence.errors.noneEnabled';
    const outOfRange = steps.some(
        (step) => !(step.delay_minutes >= MIN_STEP_DELAY_MINUTES && step.delay_minutes <= MAX_STEP_DELAY_MINUTES),
    );
    if (outOfRange) return 'skills.abandonCart.sequence.errors.delayRange';
    return null;
}
